
import React from 'react';
import { UserProfile, AppearanceMode } from '../types';

interface SettingsProps {
  user: UserProfile;
  onUpdateUser: (updates: Partial<UserProfile>) => void;
}

const Settings: React.FC<SettingsProps> = ({ user, onUpdateUser }) => {
  return (
    <div className="max-w-4xl mx-auto space-y-12">
      <section>
        <h2 className="text-2xl font-bold mb-2">Account Settings</h2>
        <p className="text-slate-500 text-sm mb-6">Manage your profile information and account security.</p>
        
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Full Name</label>
                <input 
                  type="text" 
                  value={user.name}
                  onChange={(e) => onUpdateUser({ name: e.target.value })}
                  className="w-full bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Email Address</label>
                <input 
                  type="email" 
                  value={user.email}
                  disabled
                  className="w-full bg-slate-100 dark:bg-slate-950/50 border border-slate-200 dark:border-slate-800 rounded-xl p-3 text-sm text-slate-400 cursor-not-allowed"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="appearance">
        <h2 className="text-2xl font-bold mb-2">Appearance</h2>
        <p className="text-slate-500 text-sm mb-6">Customize the look and feel of the MarketXtractor interface.</p>
        
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden shadow-sm">
          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { id: AppearanceMode.LIGHT, label: 'Light', icon: '☀️' },
                { id: AppearanceMode.DARK, label: 'Dark', icon: '🌙' },
                { id: AppearanceMode.AUTO, label: 'Auto', icon: '🖥️' }
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => onUpdateUser({ appearanceMode: mode.id })}
                  className={`flex flex-col items-center justify-center p-6 rounded-xl border-2 transition-all ${
                    user.appearanceMode === mode.id
                    ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/10 text-indigo-600 dark:text-indigo-400'
                    : 'border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-slate-500 hover:border-slate-200 dark:hover:border-slate-700'
                  }`}
                >
                  <span className="text-3xl mb-3">{mode.icon}</span>
                  <span className="font-bold text-sm">{mode.label}</span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-xs text-slate-500 italic">
              Auto mode will follow your system preference for light or dark appearance.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-2">Security</h2>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-sm">Two-Factor Authentication</h4>
            <p className="text-xs text-slate-500">Secure your account with 2FA protection.</p>
          </div>
          <button className="px-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-xs font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
            Configure
          </button>
        </div>
      </section>
    </div>
  );
};

export default Settings;
